import { useEffect, useMemo, useState } from "react";
import { useWorkspace } from "../app/workspace";
import { Icon } from "../components/icons";
import { MetricTile, BarChart, DonutChart, LineChart, ReportTable } from "../components/charts";
import { runReport, fmt, sourcesFor, getSource, type ReportDef, type Filter, type Viz, type ReportResult } from "../lib/reporting";
import {
  loadDashboards, createDashboard, updateDashboard, deleteDashboard, computeResults,
  type Dashboard, type Widget, type PersistMode,
} from "../lib/reportClient";
import type { Workspace } from "../lib/types";

const METRICS: ReportDef[] = [
  { id: "m1", title: "Portfolio ARR", source: "accounts", viz: "metric", measureId: "arr" },
  { id: "m2", title: "Avg health", source: "accounts", viz: "metric", measureId: "avgHealth" },
  { id: "m3", title: "At-risk ARR", source: "accounts", viz: "metric", measureId: "arr", filters: [{ field: "healthBand", op: "is", value: "At risk" }] },
  { id: "m4", title: "Open signals", source: "signals", viz: "metric", measureId: "count", filters: [{ field: "status", op: "is", value: "open" }] },
];

export default function Dashboards() {
  const { ws, me } = useWorkspace();
  const [mode, setMode] = useState<PersistMode>("local");
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [currentId, setCurrentId] = useState<string>("");
  const [name, setName] = useState("");
  const [visibility, setVisibility] = useState<"private" | "shared">("private");
  const [widgets, setWidgets] = useState<Widget[]>([]);
  const [dirty, setDirty] = useState(false);
  const [flash, setFlash] = useState("");
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [building, setBuilding] = useState(false);
  const [results, setResults] = useState<Record<string, ReportResult>>({});

  // initial load
  useEffect(() => {
    let alive = true;
    loadDashboards({ id: me.id, name: me.name }).then(({ mode: m, dashboards: ds }) => {
      if (!alive) return;
      setMode(m);
      setDashboards(ds);
      const first = ds.find((d) => d.id === "dash_overview") ?? ds[0];
      if (first) select(first);
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const globalFilters: Filter[] = useMemo(
    () => Object.entries(filters).filter(([, v]) => v && v !== "all").map(([field, value]) => ({ field, op: "is" as const, value })),
    [filters]
  );

  // filtered account count for the readout (cheap, always local)
  const filteredCount = useMemo(() => {
    const r = runReport(ws, { id: "_c", title: "", source: "accounts", viz: "metric", measureId: "count" }, globalFilters);
    return r.kind === "metric" ? r.value : ws.accounts.length;
  }, [ws, globalFilters]);

  // compute results (API when live, else local)
  useEffect(() => {
    let alive = true;
    const defs = [...METRICS, ...widgets];
    computeResults(mode, ws, defs, globalFilters).then((r) => { if (alive) setResults(r); });
    return () => { alive = false; };
  }, [widgets, globalFilters, mode, ws]);

  const current = dashboards.find((d) => d.id === currentId);
  const isOwner = !current || current.ownerId === me.id;

  function select(d: Dashboard) {
    setCurrentId(d.id); setName(d.name); setVisibility(d.visibility); setWidgets(d.widgets); setDirty(false);
  }
  function flashMsg(m: string) { setFlash(m); setTimeout(() => setFlash(""), 1800); }

  async function save() {
    const d = await updateDashboard(mode, currentId, { name, visibility, widgets });
    if (d) { setDashboards((ds) => ds.map((x) => (x.id === d.id ? d : x))); setDirty(false); flashMsg("Saved"); }
  }
  async function newDash() {
    const d = await createDashboard(mode, { id: me.id, name: me.name }, { name: "Untitled dashboard", visibility: "private", widgets: [] });
    setDashboards((ds) => [...ds, d]); select(d); flashMsg("Created");
  }
  async function duplicate() {
    const d = await createDashboard(mode, { id: me.id, name: me.name }, { name: `${name} (copy)`, visibility: "private", widgets });
    setDashboards((ds) => [...ds, d]); select(d); flashMsg("Duplicated");
  }
  async function remove() {
    await deleteDashboard(mode, currentId);
    const rest = dashboards.filter((d) => d.id !== currentId);
    setDashboards(rest);
    if (rest[0]) select(rest[0]);
  }
  function copyLink() {
    navigator.clipboard?.writeText(`https://app.penumbrai.ai/d/${currentId}`).then(() => flashMsg("Link copied"), () => flashMsg("Link copied"));
  }

  return (
    <div className="page">
      <div className="row between mb-16" style={{ alignItems: "flex-start" }}>
        <div>
          <p className="eyebrow">Dashboards</p>
          <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>Report on the whole database</h1>
          <p className="page__intro" style={{ marginBottom: 0 }}>
            Build widgets over any object — accounts, signals, tasks, playbooks, integrations, and every custom
            object you define. Save a board, share it with the team, and filter once; the account filter cascades
            through relationships to reshape related widgets too.
          </p>
        </div>
        <button className="btn btn--primary" onClick={() => setBuilding((v) => !v)}><Icon name="plus" size={15} /> Add report</button>
      </div>

      {/* dashboard management bar */}
      <div className="dashbar">
        <select className="input" value={currentId} onChange={(e) => { const d = dashboards.find((x) => x.id === e.target.value); if (d) select(d); }} aria-label="Dashboard">
          {dashboards.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}{d.ownerId !== me.id ? ` · ${d.ownerName}` : ""}{d.visibility === "shared" ? " ↗" : ""}
            </option>
          ))}
        </select>

        {isOwner ? (
          <>
            <input className="input" value={name} onChange={(e) => { setName(e.target.value); setDirty(true); }} style={{ minWidth: 200 }} aria-label="Dashboard name" />
            <div className="seg-control">
              <button className={visibility === "private" ? "on" : ""} onClick={() => { setVisibility("private"); setDirty(true); }}>Private</button>
              <button className={visibility === "shared" ? "on" : ""} onClick={() => { setVisibility("shared"); setDirty(true); }}>Shared</button>
            </div>
          </>
        ) : (
          <span className="chip"><Icon name="user" size={13} /> Shared by {current?.ownerName}</span>
        )}

        <span className="grow" />
        {flash && <span className="badge badge--good">{flash}</span>}
        <span className={`mode-pill ${mode === "api" ? "live" : ""}`}>
          <i /> {mode === "api" ? "Live API" : "Local demo"}
        </span>
        {current?.visibility === "shared" && <button className="btn btn--ghost btn--sm" onClick={copyLink}><Icon name="link" size={14} /> Copy link</button>}
        <button className="btn btn--ghost btn--sm" onClick={duplicate}><Icon name="layers" size={14} /> Duplicate</button>
        <button className="btn btn--ghost btn--sm" onClick={newDash}><Icon name="plus" size={14} /> New</button>
        {isOwner && <button className="btn btn--sm" onClick={save} disabled={!dirty} style={{ opacity: dirty ? 1 : 0.5 }}><Icon name="check" size={14} /> Save</button>}
        {isOwner && dashboards.length > 1 && <button className="btn btn--ghost btn--sm" onClick={remove} aria-label="Delete"><span style={{ display: "inline-flex", transform: "rotate(45deg)" }}><Icon name="plus" size={14} strokeWidth={2} /></span></button>}
      </div>

      {/* global filter bar */}
      <div className="toolbar" style={{ padding: "12px 14px", background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r)", boxShadow: "var(--shadow)", marginTop: 12 }}>
        <span className="row" style={{ gap: 6, color: "var(--muted)", fontSize: 13, fontWeight: 600 }}><Icon name="filter" size={15} /> Filters</span>
        <FilterSelect label="Region" value={filters.region} options={uniq(ws.accounts.map((a) => a.region))} onChange={(v) => setFilters((f) => ({ ...f, region: v }))} />
        <FilterSelect label="Segment" value={filters.segment} options={uniq(ws.accounts.map((a) => String(a.attributes.segment ?? "—")))} onChange={(v) => setFilters((f) => ({ ...f, segment: v }))} />
        <FilterSelect label="Health" value={filters.healthBand} options={["Healthy", "Watch", "At risk"]} onChange={(v) => setFilters((f) => ({ ...f, healthBand: v }))} />
        <FilterSelect label="Owner" value={filters.owner} options={ws.users.map((u) => u.name)} onChange={(v) => setFilters((f) => ({ ...f, owner: v }))} />
        {globalFilters.length > 0 && <button className="btn btn--ghost btn--sm" onClick={() => setFilters({})}>Clear ({globalFilters.length})</button>}
        <span className="grow" />
        <span className="faint" style={{ fontSize: 12.5 }}>{filteredCount} of {ws.accounts.length} accounts</span>
      </div>

      {building && <Builder ws={ws} globalFilters={globalFilters} onAdd={(w) => { setWidgets((x) => [...x, w]); setDirty(true); setBuilding(false); }} onClose={() => setBuilding(false)} />}

      {/* metric row */}
      <div className="stats" style={{ marginTop: 20 }}>
        {METRICS.map((m) => {
          const r = results[m.id];
          return <MetricTile key={m.id} label={m.title} value={r && r.kind === "metric" ? r.value : 0} format={r && r.kind === "metric" ? r.format : "num"} />;
        })}
      </div>

      {/* widget grid */}
      <div className="dash-grid">
        {widgets.map((w) => (
          <div key={w.id} className={`card widget span-${w.span}`}>
            {isOwner && <button className="widget-remove" onClick={() => { setWidgets((x) => x.filter((y) => y.id !== w.id)); setDirty(true); }} aria-label="Remove" style={{ transform: "rotate(45deg)" }}><Icon name="plus" size={15} strokeWidth={2} /></button>}
            <div className="card__hd"><h3>{w.title}</h3><span className="tag">{getSource(ws, w.source).label}</span></div>
            <div className="card__bd"><WidgetBody def={w} result={results[w.id]} /></div>
          </div>
        ))}
        {widgets.length === 0 && <div className="span-6 empty">This dashboard is empty. Use “Add report” to build a widget.</div>}
      </div>
    </div>
  );
}

function WidgetBody({ def, result }: { def: ReportDef; result?: ReportResult }) {
  if (!result) return <div className="empty" style={{ padding: 24 }}>…</div>;
  if (result.kind === "series") return def.viz === "donut" ? <DonutChart data={result.series} format={result.format} /> : <BarChart data={result.series} format={result.format} />;
  if (result.kind === "line") return <LineChart points={result.points} labels={result.labels} format={result.format} />;
  if (result.kind === "table") return <ReportTable columns={result.columns} rows={result.rows} />;
  if (result.kind === "metric") return <div className="stat__value">{fmt(result.value, result.format)}</div>;
  return null;
}

function FilterSelect({ label, value, options, onChange }: { label: string; value?: string; options: string[]; onChange: (v: string) => void }) {
  return (
    <select className="input" value={value ?? "all"} onChange={(e) => onChange(e.target.value)} style={{ padding: "6px 10px", fontSize: 12.5 }} aria-label={label}>
      <option value="all">{label}: All</option>
      {options.map((o) => <option key={o} value={o}>{label}: {o}</option>)}
    </select>
  );
}

/* ---------------- report builder (local preview) ---------------- */
function Builder({ ws, globalFilters, onAdd, onClose }: { ws: Workspace; globalFilters: Filter[]; onAdd: (w: Widget) => void; onClose: () => void }) {
  const [title, setTitle] = useState("New report");
  const [sourceId, setSourceId] = useState("accounts");
  const [viz, setViz] = useState<Viz>("bar");
  const [measureId, setMeasureId] = useState("arr");
  const [dimensionId, setDimensionId] = useState("segment");

  const source = getSource(ws, sourceId);
  const needsDim = viz === "bar" || viz === "donut";
  const changeSource = (id: string) => {
    const s = getSource(ws, id);
    setSourceId(id); setMeasureId(s.measures[0].id); setDimensionId(s.dims[0].id);
    if (viz === "line" && !s.supportsLine) setViz("bar");
  };

  const def: ReportDef = { id: "preview", title, source: sourceId, viz, measureId, dimensionId: needsDim ? dimensionId : undefined, limit: 6 };
  const r = runReport(ws, def, globalFilters);
  const allViz: { id: Viz; label: string; icon: string }[] = [
    { id: "bar", label: "Bar", icon: "activity" }, { id: "donut", label: "Donut", icon: "target" },
    { id: "line", label: "Line", icon: "pulse" }, { id: "table", label: "Table", icon: "grid" }, { id: "metric", label: "Metric", icon: "bolt" },
  ];
  const vizzes = allViz.filter((v) => v.id !== "line" || source.supportsLine);

  return (
    <div className="card" style={{ marginTop: 12, borderColor: "rgba(201,130,26,.35)" }}>
      <div className="card__hd">
        <div><h3>New report</h3><span className="sub">Query any object in the workspace</span></div>
        <button className="btn btn--ghost btn--sm" onClick={onClose}>Cancel</button>
      </div>
      <div className="card__bd builder">
        <div>
          <div className="builder-field"><label>Object</label>
            <select className="input" value={sourceId} onChange={(e) => changeSource(e.target.value)}>
              {sourcesFor(ws).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div className="builder-field"><label>Title</label><input className="input" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
          <div className="builder-field"><label>Visualization</label>
            <div className="viz-pick">{vizzes.map((v) => <button key={v.id} className={viz === v.id ? "on" : ""} onClick={() => setViz(v.id)}><Icon name={v.icon} size={16} />{v.label}</button>)}</div>
          </div>
          <div className="builder-field"><label>Measure</label>
            <select className="input" value={measureId} onChange={(e) => setMeasureId(e.target.value)}>{source.measures.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}</select>
          </div>
          {needsDim && (
            <div className="builder-field"><label>Group by</label>
              <select className="input" value={dimensionId} onChange={(e) => setDimensionId(e.target.value)}>{source.dims.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}</select>
            </div>
          )}
          <button className="btn btn--primary" style={{ width: "100%", justifyContent: "center" }}
            onClick={() => onAdd({ ...def, id: `w${Date.now()}`, span: viz === "table" || viz === "line" ? 6 : 3 })}>
            <Icon name="plus" size={15} /> Add to dashboard
          </button>
        </div>
        <div>
          <div className="ff-mono faint" style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 10 }}>Live preview</div>
          <div style={{ minHeight: 180 }}>
            {r.kind === "series" && (viz === "donut" ? <DonutChart data={r.series} format={r.format} /> : <BarChart data={r.series} format={r.format} />)}
            {r.kind === "line" && <LineChart points={r.points} labels={r.labels} format={r.format} />}
            {r.kind === "table" && <ReportTable columns={r.columns} rows={r.rows} />}
            {r.kind === "metric" && <div className="stat__value" style={{ fontSize: 44 }}>{fmt(r.value, r.format)}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function uniq(xs: string[]): string[] { return [...new Set(xs)].sort(); }
